import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Tabs,
  Button,
  Modal,
  Typography,
  Space,
  message,
  Spin,
  Input,
  Dropdown,
} from "antd";
import type { TabsProps, MenuProps, UploadProps } from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HeartOutlined,
  LineChartOutlined,
  MoreOutlined,
  PlusOutlined,
  ShareAltOutlined,
  RiseOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useGate } from "../Subscription/entitlements";
import DashboardTemplate from "./DashboardTemplate";
import HeaderSection from "./components/HeaderSection";
import PropertiesDrawer from "./components/PropertiesDrawer";
import GenerateReportPreview from "./modals/GenerateReportPreview";
import { humanizeFileName } from "../../utils/getFilenameforDashboard";
import {
  acceptDashboardTemplate,
  getTabCards,
  getCardData,
  updateDashboardHeader,
  updateDashboardFooter,
  getDashboardById,
  getDashboardColumns,
  addInsightCard,
  addTextBoxCard,
  addImageCard,
  createDashboardTab,
  uploadDashboardImage,
  deleteDashboardCard,
  deleteDashboardTab,
  updateDashboardTab,
  exportDashboard,
  updateDashboardCard,
  generateDashboardReport,
} from "./services/dashboard.service";
import type {
  DashboardAcceptedApi,
  DashboardTabApi,
  DashboardCardApi,
  DashboardCardWithData,
  DashboardColumnApi,
} from "../../types/types";
import {
  CardType,
  FooterOptions,
  HeaderOptions,
  DEFAULT_HEADER,
  DEFAULT_FOOTER,
} from "../../types/dashboard";
import type { GenerateReportResponse } from "../../types/report";
import AddInsightModal from "./modals/AddInsightModal";
import ShareModal from "./modals/ShareModal";

const { Text, Title } = Typography;

const toHeaderOptions = (dashboard: DashboardAcceptedApi): HeaderOptions => {
  const h = dashboard.header_config || {};
  return {
    ...DEFAULT_HEADER,
    fontSize: Number(h.font_size ?? DEFAULT_HEADER.fontSize),
    fontFamily: h.font_type ?? DEFAULT_HEADER.fontFamily,
    titleColor: h.font_color ?? DEFAULT_HEADER.titleColor,
    subtitleColor: h.font_color ?? DEFAULT_HEADER.subtitleColor,
    paddingLeft: Number(
      String(h.padding_left ?? DEFAULT_HEADER.paddingLeft).replace("px", ""),
    ),
    paddingRight: Number(
      String(h.padding_right ?? DEFAULT_HEADER.paddingRight).replace("px", ""),
    ),
    backgroundColor: h.background_color ?? DEFAULT_HEADER.backgroundColor,
    borderColor: h.border_color ?? DEFAULT_HEADER.borderColor,
    borderWidth: Number(h.border_stroke ?? DEFAULT_HEADER.borderWidth),
    radius: Number(h.border_radius ?? DEFAULT_HEADER.radius),
    logoUrl: h.image_url ?? "",
    logoAlign: String(h.image_alignment ?? "Right").toLowerCase(),
    headerImageAlign: String(h.image_alignment ?? "Right").toLowerCase(),
  };
};

const toFooterOptions = (dashboard: DashboardAcceptedApi): FooterOptions => {
  const f = dashboard.footer_config || {};
  return {
    ...DEFAULT_FOOTER,
    textColor: f.font_color ?? DEFAULT_FOOTER.textColor,
    fontSize: Number(f.font_size ?? DEFAULT_FOOTER.fontSize),
    fontFamily: f.font_type ?? DEFAULT_FOOTER.fontFamily,
    paddingX: Number(
      String(f.padding_left ?? DEFAULT_FOOTER.paddingX).replace("px", ""),
    ),
    paddingY: DEFAULT_FOOTER.paddingY,
    paddingLeft: Number(
      String(
        f.padding_left ?? DEFAULT_FOOTER.paddingLeft ?? DEFAULT_FOOTER.paddingX,
      ).replace("px", ""),
    ),
    paddingRight: Number(
      String(
        f.padding_right ??
          DEFAULT_FOOTER.paddingRight ??
          DEFAULT_FOOTER.paddingX,
      ).replace("px", ""),
    ),
    lineHeight: f.line_height ?? DEFAULT_FOOTER.lineHeight,
    backgroundColor: f.background_color ?? DEFAULT_FOOTER.backgroundColor,
    borderColor: f.border_color ?? DEFAULT_FOOTER.borderColor,
    borderWidth: Number(f.border_stroke ?? DEFAULT_FOOTER.borderWidth),
    radius: Number(f.border_radius ?? DEFAULT_FOOTER.radius),
    imageUrl: f.image_url ?? "",
    imageAlign: String(f.image_alignment ?? "right").toLowerCase() as
      | "left"
      | "right",
  };
};

const buildCardDataPayload = (
  card: DashboardCardApi,
  drillDownFilters: Record<string, any> = {},
) => ({
  filters: drillDownFilters,
  metrics: card.metrics ?? undefined,
  dimensions: card.dimensions ?? undefined,
  num_bins: card.card_type === "histogram" ? 10 : undefined,
});

const DashboardPreview: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const gate = useGate();
  const { id: projectId } = useParams<{ id: string }>();
  const { state } = useLocation() as {
    state?: {
      sourceType?: string;
      url?: string;
      fileName?: string;
      dashboardId?: string;
      backendResponse?: any;
      openExisting?: boolean;
    };
  };

  const dashboardId = state?.dashboardId;
  const fileName = state?.fileName || state?.url || "";
  const openExisting = state?.openExisting;
  const isBscPinned = localStorage.getItem(`bsc_pinned_${projectId}`) === "1";

  const [showChooser, setShowChooser] = useState(!openExisting);
  const [loading, setLoading] = useState(false); // për accept/fetch inicial
  const [saving, setSaving] = useState(false); // për save settings
  const [dashboard, setDashboard] = useState<DashboardAcceptedApi | null>(null);
  const [cardsByTab, setCardsByTab] = useState<
    Record<string, DashboardCardWithData[]>
  >({});
  const [activeKey, setActiveKey] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<CardType | null>(null);
  const [textTemp, setTextTemp] = useState("");
  const [columns, setColumns] = useState<DashboardColumnApi[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>();
  const [selectedDimension, setSelectedDimension] = useState<
    string | undefined
  >();
  const [selectedFilterColumn, setSelectedFilterColumn] = useState<
    string | undefined
  >();
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>(
    [],
  );

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  const [drillDownFilters, setDrillDownFilters] = useState<Record<string, any>>(
    {},
  );
  const [renameTabOpen, setRenameTabOpen] = useState(false);
  const [tabToRename, setTabToRename] = useState<DashboardTabApi | null>(null);
  const [renameTabValue, setRenameTabValue] = useState("");

  const [customCardTitle, setCustomCardTitle] = useState("");

  // Header state (lifted from DashboardTemplate)
  const [dashTitle, setDashTitle] = useState("");
  const [dashSubtitle, setDashSubtitle] = useState("");
  const [dashEditingTitle, setDashEditingTitle] = useState(false);
  const [dashHeaderOptions, setDashHeaderOptions] =
    useState<HeaderOptions>(DEFAULT_HEADER);
  const [dashFooterLeft, setDashFooterLeft] = useState("");
  const [dashFooterRight, setDashFooterRight] = useState(() =>
    t("preview.defaults.footerRight", { year: new Date().getFullYear() }),
  );
  const [dashFooterOptions, setDashFooterOptions] =
    useState<FooterOptions>(DEFAULT_FOOTER);

  // Properties drawer state
  const [propOpen, setPropOpen] = useState(false);

  // Share modal state
  const [shareOpen, setShareOpen] = useState(false);

  // Generate report state
  const [generateReportOpen, setGenerateReportOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] =
    useState("executive_brief");
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportData, setReportData] = useState<GenerateReportResponse | null>(
    null,
  );
  const [reportLoading, setReportLoading] = useState(false);

  const cardLabels: Record<
    CardType,
    { label: string; render: (c: any) => React.ReactNode }
  > = {
    text: {
      label: t("preview.cardTypes.text.label"),
      render: () => <div>{t("preview.cardTypes.text.preview")}</div>,
    },
    image: {
      label: t("preview.cardTypes.image.label"),
      render: () => <div>{t("preview.cardTypes.image.preview")}</div>,
    },
    critical: {
      label: t("preview.cardTypes.critical.label"),
      render: () => <div>{t("preview.cardTypes.critical.preview")}</div>,
    },
    trend: {
      label: t("preview.cardTypes.trend.label"),
      render: () => <div>{t("preview.cardTypes.trend.preview")}</div>,
    },
    progress: {
      label: t("preview.cardTypes.progress.label"),
      render: () => <div>{t("preview.cardTypes.progress.preview")}</div>,
    },
    anomaly: {
      label: t("preview.cardTypes.anomaly.label"),
      render: () => <div>{t("preview.cardTypes.anomaly.preview")}</div>,
    },
    sentiment: {
      label: t("preview.cardTypes.sentiment.label"),
      render: () => <div>{t("preview.cardTypes.sentiment.preview")}</div>,
    },
    geo: {
      label: t("preview.cardTypes.geo.label"),
      render: () => <div>{t("preview.cardTypes.geo.preview")}</div>,
    },
    histogram: {
      label: t("preview.cardTypes.histogram.label"),
      render: () => <div>{t("preview.cardTypes.histogram.preview")}</div>,
    },
    treemap: {
      label: t("preview.cardTypes.treemap.label"),
      render: () => <div>{t("preview.cardTypes.treemap.preview")}</div>,
    },
    heatmap: {
      label: t("preview.cardTypes.heatmap.label"),
      render: () => <div>{t("preview.cardTypes.heatmap.preview")}</div>,
    },
    location: {
      label: t("preview.cardTypes.location.label"),
      render: () => <div>{t("preview.cardTypes.location.preview")}</div>,
    },
    wordCloud: {
      label: t("preview.cardTypes.wordCloud.label"),
      render: () => null,
    },
    bsc: {
      label: t("preview.cardTypes.bsc.label"),
      render: () => <div>{t("preview.cardTypes.bsc.preview")}</div>,
    },
  };

  const refreshTabCardsWithData = useCallback(
    async (tabId: string, drillDownFilters: Record<string, any> = {}) => {
      const cards = await getTabCards(tabId);

      const cardsWithData: DashboardCardWithData[] = await Promise.all(
        cards.map(async (card: DashboardCardApi) => {
          try {
            if (card.card_type === "image" || card.card_type === "text_box") {
              return {
                ...card,
                data: null,
              };
            }

            const payload = buildCardDataPayload(card, drillDownFilters);
            const data = await getCardData(card.id, payload);

            return {
              ...card,
              data,
              isLoading: false,
              error: null,
            };
          } catch (err) {
            console.error(`Failed to load data for card ${card.id}`, err);
            return {
              ...card,
              data: null,
              isLoading: false,
              error: t("preview.messages.cardDataLoadFailed"),
            };
          }
        }),
      );

      const bscStored: any[] = JSON.parse(
        localStorage.getItem(`bsc_dashboard_cards_${tabId}`) || "[]",
      );
      const bscCards: DashboardCardWithData[] = bscStored.map((c) => ({
        ...c,
        data: null,
      }));

      return [...cardsWithData, ...bscCards];
    },
    [t],
  );

  const openRenameTabModal = (tab: DashboardTabApi) => {
    setTabToRename(tab);
    setRenameTabValue(tab.name || "");
    setRenameTabOpen(true);
  };

  const loadDashboardColumns = useCallback(async () => {
    if (!dashboardId) return;

    try {
      const response = await getDashboardColumns(dashboardId);
      const raw = Array.isArray(response)
        ? response
        : Array.isArray(response?.columns)
          ? response.columns
          : [];
      // Filter out any non-column objects (e.g. Pydantic error entries)
      setColumns(raw.filter((c: any) => c?.name != null && c?.dtype != null));
    } catch (error) {
      console.error("Failed to load dashboard columns", error);
      setColumns([]);
    }
  }, [dashboardId]);

  const loadExistingDashboard = useCallback(async () => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      setLoading(true);

      const existingDashboard = await getDashboardById(dashboardId);
      await loadDashboardColumns();

      const tabEntries = await Promise.all(
        (existingDashboard.tabs || []).map(async (tab: DashboardTabApi) => {
          const cardsWithData = await refreshTabCardsWithData(tab.id);
          return [tab.id, cardsWithData] as const;
        }),
      );

      const nextCardsByTab = Object.fromEntries(tabEntries);

      setDashboard(existingDashboard);
      setCardsByTab(nextCardsByTab);
      setActiveKey(existingDashboard.tabs?.[0]?.id || "");
      setShowChooser(false);
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t("preview.messages.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => {
    if (openExisting && dashboardId) {
      loadExistingDashboard();
    }
  }, [openExisting, dashboardId, loadExistingDashboard]);

  useEffect(() => {
    if (!dashboard) return;
    setDashTitle(
      dashboard.header_config?.title ||
        humanizeFileName(fileName) ||
        t("preview.defaults.dashboardTitle"),
    );
    setDashSubtitle(dashboard.header_config?.subtitle || "");
    setDashHeaderOptions(toHeaderOptions(dashboard));
    setDashFooterLeft(
      dashboard.footer_config?.title || t("preview.defaults.footerText"),
    );
    setDashFooterOptions(toFooterOptions(dashboard));
  }, [dashboard, fileName, t]);

  const acceptTemplate = async () => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      setLoading(true);

      const acceptedDashboard = await acceptDashboardTemplate(dashboardId);
      await loadDashboardColumns();

      const tabEntries = await Promise.all(
        acceptedDashboard.tabs.map(async (tab: DashboardTabApi) => {
          const cardsWithData = await refreshTabCardsWithData(tab.id);
          return [tab.id, cardsWithData] as const;
        }),
      );

      const nextCardsByTab = Object.fromEntries(tabEntries);

      setDashboard(acceptedDashboard);
      setCardsByTab(nextCardsByTab);
      setActiveKey(acceptedDashboard.tabs?.[0]?.id || "");
      setShowChooser(false);

      message.success(t("preview.messages.templateAccepted"));
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t("preview.messages.acceptTemplateFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const chooseOwn = async () => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      setLoading(true);

      let existingDashboard = await getDashboardById(dashboardId);
      await loadDashboardColumns();

      let tabs = existingDashboard.tabs || [];

      if (!tabs.length) {
        const newTab = await createDashboardTab(dashboardId, {
          name: t("preview.defaults.overviewTab"),
        });

        tabs = [newTab];
        existingDashboard = {
          ...existingDashboard,
          tabs,
        };
      }

      const emptyCardsByTab = Object.fromEntries(
        tabs.map((tab: DashboardTabApi) => [tab.id, []]),
      );

      setDashboard(existingDashboard);
      setCardsByTab(emptyCardsByTab);
      setActiveKey(tabs[0]?.id || "");
      setShowChooser(false);

      message.success(t("preview.messages.chooseOwnSuccess"));
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t("preview.messages.chooseOwnFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImageForProps = async (file: File): Promise<string> => {
    if (!dashboardId) return "";
    try {
      const response = await uploadDashboardImage(dashboardId, file);
      return response?.image_url || "";
    } catch {
      message.error(t("preview.messages.imageUploadFailed"));
      return "";
    }
  };

  const logoUploadProps: UploadProps = {
    beforeUpload: async (file) => {
      const url = await handleUploadImageForProps(file as File);
      if (url) setDashHeaderOptions((prev) => ({ ...prev, logoUrl: url }));
      return false;
    },
    maxCount: 1,
    showUploadList: false,
  };

  const headerImageUploadProps: UploadProps = {
    beforeUpload: async (file) => {
      const url = await handleUploadImageForProps(file as File);
      if (url)
        setDashHeaderOptions((prev) => ({ ...prev, headerImageDataUrl: url }));
      return false;
    },
    maxCount: 1,
    showUploadList: false,
  };

  const footerImageUploadProps: UploadProps = {
    beforeUpload: async (file) => {
      const url = await handleUploadImageForProps(file as File);
      if (url) setDashFooterOptions((prev) => ({ ...prev, imageUrl: url }));
      return false;
    },
    maxCount: 1,
    showUploadList: false,
  };

  const px = (value: string | number | undefined, fallback = 0) => {
    if (value === undefined || value === null || value === "")
      return String(fallback);
    if (typeof value === "number") return String(value);
    return value.replace("px", "").trim();
  };

  const handleSaveDashboardSettings = async (payload: {
    title: string;
    subtitle: string;
    headerOptions: HeaderOptions;
    footerLeft: string;
    footerRight: string;
    footerOptions: FooterOptions;
  }) => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      setSaving(true);

      const headerPayload = {
        title: payload.title,
        subtitle: payload.subtitle,
        font_color: payload.headerOptions.titleColor || "#333333",
        font_size: Number(payload.headerOptions.fontSize || 16),
        font_type: payload.headerOptions.fontFamily || "Poppins",

        padding_left: px(payload.headerOptions.paddingLeft, 16),
        padding_right: px(payload.headerOptions.paddingRight, 16),

        image_url:
          payload.headerOptions.logoUrl ||
          payload.headerOptions.headerImageUrl ||
          "",

        image_alignment:
          payload.headerOptions.logoAlign ||
          payload.headerOptions.headerImageAlign ||
          "Right",

        background_color: payload.headerOptions.backgroundColor || "#FFFFFF",
        border_color: payload.headerOptions.borderColor || "#CCCCCC",
        border_radius: Number(payload.headerOptions.radius || 12),
        border_stroke: Number(payload.headerOptions.borderWidth || 0),
      };

      const footerPayload = {
        title: payload.footerLeft || "",
        right_text: payload.footerRight || "",

        font_color: payload.footerOptions.textColor || "#666666",
        font_size: Number(payload.footerOptions.fontSize || 12),
        font_type: payload.footerOptions.fontFamily || "Poppins",

        line_height: payload.footerOptions.lineHeight || "normal",

        padding_left: px(
          payload.footerOptions.paddingLeft ?? payload.footerOptions.paddingX,
          24,
        ),
        padding_right: px(
          payload.footerOptions.paddingRight ?? payload.footerOptions.paddingX,
          24,
        ),

        image_url: payload.footerOptions.imageUrl || "",
        image_alignment: payload.footerOptions.imageAlign || "right",

        background_color: payload.footerOptions.backgroundColor || "#F5F5F5",
        border_color: payload.footerOptions.borderColor || "#999999",
        border_stroke: Number(payload.footerOptions.borderWidth || 0),
        border_radius: Number(payload.footerOptions.radius || 12),
      };
      await updateDashboardHeader(dashboardId, headerPayload);
      await updateDashboardFooter(dashboardId, footerPayload);

      message.success(t("preview.messages.settingsSaved"));
    } catch (error: any) {
      console.error("Failed to save dashboard properties:", error);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("preview.messages.settingsSaveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUploadHeaderImage = async (file: File) => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return "";
    }

    try {
      const response = await uploadDashboardImage(dashboardId, file);
      return response?.image_url || "";
    } catch (error: any) {
      console.error("Header image upload failed:", error);
      message.error(t("preview.messages.imageUploadFailed"));
      return "";
    }
  };

  const handleAddTab = async () => {
    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      const nextTabNumber = (dashboard?.tabs?.length || 0) + 1;

      const newTab = await createDashboardTab(dashboardId, {
        name: t("preview.defaults.tabNumber", { number: nextTabNumber }),
      });

      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              tabs: [...(prev.tabs || []), newTab],
            }
          : prev,
      );

      setCardsByTab((prev) => ({
        ...prev,
        [newTab.id]: [],
      }));

      setActiveKey(newTab.id);
      message.success(t("preview.messages.tabCreated"));
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("preview.messages.tabCreateFailed"),
      );
    }
  };

  const handleDeleteTab = async (tabId: string) => {
    if (!dashboard || !dashboard.tabs || dashboard.tabs.length <= 1) {
      message.warning(t("preview.messages.atLeastOneTabRequired"));
      return;
    }

    try {
      await deleteDashboardTab(tabId);

      const nextTabs = (dashboard.tabs || []).filter((tab) => tab.id !== tabId);

      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              tabs: nextTabs,
            }
          : prev,
      );

      setCardsByTab((prev) => {
        const next = { ...prev };
        delete next[tabId];
        return next;
      });

      if (activeKey === tabId) {
        setActiveKey(nextTabs[0]?.id || "");
      }

      message.success(t("preview.messages.tabDeleted"));
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("preview.messages.tabDeleteFailed"),
      );
    }
  };

  const handleApplyDrillDown = useCallback(
    async (tabId: string, filters: Record<string, any>) => {
      try {
        setLoading(true);
        setDrillDownFilters(filters);

        const refreshedCards = await refreshTabCardsWithData(tabId, filters);

        setCardsByTab((prev) => ({
          ...prev,
          [tabId]: refreshedCards,
        }));
      } catch (error) {
        console.error(error);
        message.error(t("preview.messages.drillDownApplyFailed"));
      } finally {
        setLoading(false);
      }
    },
    [refreshTabCardsWithData, t],
  );

  const handleClearDrillDown = useCallback(async () => {
    if (!activeKey) return;

    try {
      setLoading(true);
      setDrillDownFilters({});

      const refreshedCards = await refreshTabCardsWithData(activeKey, {});

      setCardsByTab((prev) => ({
        ...prev,
        [activeKey]: refreshedCards,
      }));
    } catch (error) {
      console.error(error);
      message.error(t("preview.messages.drillDownClearFailed"));
    } finally {
      setLoading(false);
    }
  }, [activeKey, refreshTabCardsWithData, t]);

  const handleUpdateCard = async (
    tabId: string,
    cardId: string,
    payload: Record<string, any>,
  ) => {
    try {
      await updateDashboardCard(cardId, payload);

      const refreshedCards = await refreshTabCardsWithData(
        tabId,
        activeKey === tabId ? drillDownFilters : {},
      );

      setCardsByTab((prev) => ({
        ...prev,
        [tabId]: refreshedCards,
      }));

      message.success(t("preview.messages.cardUpdated"));
    } catch (error: any) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      const errMsg = Array.isArray(detail)
        ? detail
            .map((d: any) => d?.msg || t("preview.messages.validationError"))
            .join("; ")
        : typeof detail === "string"
          ? detail
          : error?.response?.data?.message ||
            t("preview.messages.cardUpdateFailed");
      message.error(errMsg);
    }
  };

  // Captures a single card/header/footer block into its own canvas — one
  // block per html2canvas call, rather than one screenshot of the whole
  // page, so a card is never sliced in half across a PDF page break.
  const captureBlockToCanvas = (el: HTMLElement) =>
    html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

  const collectExportBlocks = (container: HTMLElement): HTMLElement[] => {
    const blocks: HTMLElement[] = [];
    const headerEl = document.getElementById("dashboard-header-printable");
    if (headerEl && headerEl.offsetHeight > 0) blocks.push(headerEl);

    Array.from(container.children).forEach((child) => {
      const el = child as HTMLElement;
      const grid = el.querySelector<HTMLElement>(".dashboard-cards-grid");
      if (grid) {
        Array.from(grid.children).forEach((card) => {
          if ((card as HTMLElement).offsetHeight > 0) {
            blocks.push(card as HTMLElement);
          }
        });
      } else if (el.offsetHeight > 0) {
        blocks.push(el);
      }
    });

    return blocks;
  };

  const handleExportDashboardPdf = async () => {
    if (!tabItems || tabItems.length === 0) {
      message.error(t("preview.messages.noValidTab"));
      return;
    }

    const style = document.createElement("style");
    style.innerHTML = `
      .dashboard-card-actions { visibility: hidden !important; }
      .card-live-view { display: none !important; }
      .card-pdf-snapshot { display: block !important; }
    `;
    document.head.appendChild(style);

    try {
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const pdfContentWidth = pageWidth - margin * 2;
      const maxContentHeight = pageHeight - margin * 2;
      const pdf = new jsPDF({ unit: "mm", format: "a4" });

      // First pass: capture all tabs and measure canvas widths
      const tabCanvases: Array<{ tabId: string; canvas: HTMLCanvasElement }> = [];

      for (const tab of tabItems) {
        const tabId = String(tab.key);
        setActiveKey(tabId);
        await new Promise((resolve) => setTimeout(resolve, 300));

        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.left = "-9999px";
        tempContainer.style.top = "-9999px";
        tempContainer.style.width = "1200px";
        tempContainer.style.minWidth = "1200px";
        tempContainer.style.maxWidth = "1200px";
        tempContainer.style.background = "white";
        tempContainer.style.display = "block";
        tempContainer.style.boxSizing = "border-box";
        tempContainer.style.overflow = "visible";

        const headerEl = document.getElementById("dashboard-header-printable");
        const tabEl = document.getElementById(`dashboard-printable-${tabId}`);
        if (!tabEl) continue;

        if (headerEl) {
          const clonedHeader = headerEl.cloneNode(true) as HTMLElement;
          clonedHeader.style.width = "100%";
          clonedHeader.style.boxSizing = "border-box";
          tempContainer.appendChild(clonedHeader);
        }
        const clonedTab = tabEl.cloneNode(true) as HTMLElement;
        clonedTab.style.width = "100%";
        clonedTab.style.boxSizing = "border-box";
        tempContainer.appendChild(clonedTab);

        document.body.appendChild(tempContainer);
        await new Promise((resolve) => setTimeout(resolve, 50));

        const canvas = await captureBlockToCanvas(tempContainer);
        tabCanvases.push({ tabId, canvas });
        document.body.removeChild(tempContainer);
      }

      // Find max canvas width to normalize all tabs to same scale
      const maxCanvasWidth = Math.max(...tabCanvases.map((t) => t.canvas.width));
      const mmPerPixel = pdfContentWidth / maxCanvasWidth;

      // Second pass: add all tabs to PDF with normalized widths
      let isFirstTab = true;
      for (const { canvas } of tabCanvases) {
        const imgWidth = canvas.width * mmPerPixel;
        const imgHeight = canvas.height * mmPerPixel;
        const imgData = canvas.toDataURL("image/png");

        // If too tall, scale both proportionally
        if (imgHeight > maxContentHeight) {
          const scaleFactor = maxContentHeight / imgHeight;
          const scaledWidth = imgWidth * scaleFactor;
          const scaledHeight = imgHeight * scaleFactor;

          if (!isFirstTab) pdf.addPage();
          isFirstTab = false;
          const xPos = margin + (pdfContentWidth - scaledWidth) / 2;
          pdf.addImage(imgData, "PNG", xPos, margin, scaledWidth, scaledHeight);
        } else {
          if (!isFirstTab) pdf.addPage();
          isFirstTab = false;
          const xPos = margin + (pdfContentWidth - imgWidth) / 2;
          pdf.addImage(imgData, "PNG", xPos, margin, imgWidth, imgHeight);
        }
      }

      pdf.save("dashboard-export.pdf");
      message.success(t("preview.messages.exportSuccess", { format: "PDF" }));
    } catch (error: any) {
      console.error(error);
      message.error(t("preview.messages.exportFailed"));
    } finally {
      style.remove();
    }
  };

  const handleExportDashboard = async (
    format: "csv" | "xlsx" | "pdf" = "csv",
    tabId?: string,
  ) => {
    const resolvedTabId =
      tabId || activeKey || String(tabItems?.[0]?.key ?? "") || undefined;

    if (format === "pdf") {
      await handleExportDashboardPdf();
      return;
    }

    if (!dashboardId) {
      message.error(t("preview.messages.dashboardIdMissing"));
      return;
    }

    try {
      const blob = await exportDashboard(dashboardId, format, resolvedTabId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dashboard-export.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(
        t("preview.messages.exportSuccess", { format: format.toUpperCase() }),
      );
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("preview.messages.exportFailed"),
      );
    }
  };

  const handleDeleteCard = useCallback(
    async (tabId: string, cardId: string) => {
      try {
        await deleteDashboardCard(cardId);

        setCardsByTab((prev) => ({
          ...prev,
          [tabId]: (prev[tabId] || []).filter((card) => card.id !== cardId),
        }));

        message.success(t("preview.messages.cardDeleted"));
      } catch (error: any) {
        console.error(error);
        message.error(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            t("preview.messages.cardDeleteFailed"),
        );
      }
    },
    [t],
  );
  const handleRenameTab = async () => {
    if (!tabToRename) return;

    const trimmedName = renameTabValue.trim();

    if (!trimmedName) {
      message.warning(t("preview.messages.tabNameRequired"));
      return;
    }

    try {
      const updatedTab = await updateDashboardTab(tabToRename.id, {
        name: trimmedName,
      });

      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              tabs: (prev.tabs || []).map((tab) =>
                tab.id === tabToRename.id ? { ...tab, ...updatedTab } : tab,
              ),
            }
          : prev,
      );

      setRenameTabOpen(false);
      setTabToRename(null);
      setRenameTabValue("");

      message.success(t("preview.messages.tabRenamed"));
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          t("preview.messages.tabRenameFailed"),
      );
    }
  };

  const tabItems: TabsProps["items"] = useMemo(() => {
    if (!dashboard) return [];

    const tabsToRender =
      dashboard.tabs && dashboard.tabs.length > 0
        ? dashboard.tabs
        : [
            {
              id: "temp-tab",
              name: t("preview.defaults.tabNumber", { number: 1 }),
            } as DashboardTabApi,
          ];

    return tabsToRender.map((tab) => ({
      key: tab.id,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>{tab.name}</span>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "rename",
                  icon: <EditOutlined />,
                  label: t("preview.tabMenu.rename"),
                },
                ...(dashboard.tabs && dashboard.tabs.length > 1
                  ? [
                      {
                        key: "delete",
                        icon: <CloseOutlined />,
                        label: t("preview.tabMenu.delete"),
                        danger: true,
                      },
                    ]
                  : []),
              ],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === "rename") openRenameTabModal(tab);
                if (key === "delete") handleDeleteTab(tab.id);
              },
            }}
          >
            <MoreOutlined
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: 18,
                color: "#4A1C96",
                cursor: "pointer",
                fontWeight: 700,
              }}
            />
          </Dropdown>
        </div>
      ),
      closable: false,
      children: (
        <DashboardTemplate
          key={tab.id}
          tab={tab}
          dashboard={dashboard}
          cards={cardsByTab[tab.id] || []}
          onOpenAdd={() => setAddOpen(true)}
          onDeleteCard={(cardId) => handleDeleteCard(tab.id, cardId)}
          onApplyDrillDown={(filters) => handleApplyDrillDown(tab.id, filters)}
          onClearDrillDown={handleClearDrillDown}
          drillDownFilters={drillDownFilters}
          columns={columns}
          onUpdateCard={(cardId, payload) =>
            handleUpdateCard(tab.id, cardId, payload)
          }
        />
      ),
    }));
  }, [
    dashboard,
    cardsByTab,
    columns,
    drillDownFilters,
    handleApplyDrillDown,
    handleClearDrillDown,
    handleDeleteCard,
    handleUpdateCard,
    t,
  ]);

  const insightTypeMap: Record<string, string> = {
    critical: "critical_metric",
    trend: "trend_analysis",
    progress: "progress",
    anomaly: "anomaly_detection",
    sentiment: "sentiment_overview",
    geo: "geographic_performance_hotspots",
    histogram: "histogram",
    treemap: "treemap",
    heatmap: "heatmap",
    location: "location_map",
    wordCloud: "word_cloud",
  };

  const handleAddInsightCard = async () => {
    if (!selectedType) {
      message.warning(t("preview.messages.selectCardType"));
      return;
    }

    let targetTabId = activeKey;

    if (!targetTabId || targetTabId === "temp-tab") {
      message.warning(t("preview.messages.noValidTab"));
      return;
    }

    const activeTabCards = cardsByTab[targetTabId] || [];

    if (activeTabCards.length >= 10) {
      if (!dashboardId) {
        message.warning(t("preview.messages.dashboardIdMissing"));
        return;
      }

      const nextTabNumber = (dashboard?.tabs?.length || 0) + 1;

      const newTab = await createDashboardTab(dashboardId, {
        name: t("preview.defaults.tabNumber", { number: nextTabNumber }),
      });

      setDashboard((prev) =>
        prev
          ? {
              ...prev,
              tabs: [...(prev.tabs || []), newTab],
            }
          : prev,
      );

      setCardsByTab((prev) => ({
        ...prev,
        [newTab.id]: [],
      }));

      setActiveKey(newTab.id);
      targetTabId = newTab.id;

      message.info(
        t("preview.messages.tabFullNewTabCreated", { tabName: newTab.name }),
      );
    }

    const metricRequiredTypes = [
      "critical",
      "trend",
      "progress",
      "anomaly",
      "histogram",
      "treemap",
      "heatmap",
      "geo",
    ];

    const singleColumnRequiredTypes = ["sentiment", "wordCloud", "location"];

    const dimensionRequiredTypes = [
      "trend",
      "anomaly",
      "treemap",
      "heatmap",
      "geo",
    ];
    if (metricRequiredTypes.includes(selectedType) && !selectedMetric) {
      message.warning(t("preview.messages.selectMetricColumn"));
      return;
    }

    if (singleColumnRequiredTypes.includes(selectedType) && !selectedMetric) {
      message.warning(t("preview.messages.selectColumn"));
      return;
    }

    if (dimensionRequiredTypes.includes(selectedType) && !selectedDimension) {
      message.warning(t("preview.messages.selectDimensionColumn"));
      return;
    }
    try {
      setLoading(true);

      const title =
        customCardTitle.trim() ||
        cardLabels[selectedType]?.label ||
        t("preview.defaults.insightCardTitle");

      const metrics =
        selectedMetric && selectedType !== "text" && selectedType !== "image"
          ? [
              {
                name: selectedMetric,
                column: selectedMetric,
                aggregation:
                  selectedType === "histogram"
                    ? "count"
                    : selectedType === "critical"
                      ? "sum"
                      : selectedType === "progress"
                        ? "avg"
                        : "sum",
              },
            ]
          : undefined;

      const dimensions =
        selectedDimension && selectedType !== "text" && selectedType !== "image"
          ? [
              {
                name: selectedDimension,
                column: selectedDimension,
              },
            ]
          : undefined;

      const cardFilters =
        selectedFilterColumn && selectedFilterValues.length
          ? {
              [selectedFilterColumn]: {
                type: selectedFilterValues.length > 1 ? "in" : "equals",
                value:
                  selectedFilterValues.length > 1
                    ? selectedFilterValues
                    : selectedFilterValues[0],
              },
            }
          : undefined;

      if (selectedType === "bsc") {
        const bscCard = {
          id: `bsc_${Date.now()}`,
          card_type: "bsc",
          title: t("preview.cardTypes.bsc.label"),
          project_id: projectId,
        };
        const stored = JSON.parse(
          localStorage.getItem(`bsc_dashboard_cards_${targetTabId}`) || "[]",
        );
        localStorage.setItem(
          `bsc_dashboard_cards_${targetTabId}`,
          JSON.stringify([...stored, bscCard]),
        );
        setCardsByTab((prev) => ({
          ...prev,
          [targetTabId]: [
            ...(prev[targetTabId] || []),
            { ...bscCard, data: null } as any,
          ],
        }));
        setAddOpen(false);
        setSelectedType(null);
        message.success(t("preview.messages.bscCardAdded"));
        return;
      } else if (selectedType === "text") {
        await addTextBoxCard(targetTabId, {
          title,
          text_content: textTemp,
          config: { width: 6, height: 2 },
        });
      } else if (selectedType === "image") {
        if (!dashboardId) {
          message.warning(t("preview.messages.dashboardIdMissing"));
          return;
        }

        if (!selectedImageFile) {
          message.warning(t("preview.messages.selectImageFirst"));
          return;
        }

        const uploadResponse = await uploadDashboardImage(
          dashboardId,
          selectedImageFile,
        );

        console.log("UPLOAD RESPONSE:", uploadResponse);

        const imageUrl = uploadResponse?.image_url;

        if (!imageUrl) {
          throw new Error(t("preview.messages.imageUploadFailedError"));
        }

        const imagePayload = {
          title,
          image_path: imageUrl,
          description: "",
          order_index: 0,
          config: { width: 6, height: 2 },
        };

        console.log("IMAGE CARD PAYLOAD:", imagePayload);

        await addImageCard(targetTabId, imagePayload);
      } else {
        const cardTypeForApi = insightTypeMap[selectedType];

        let selectedColumns: string[] = [];

        if (selectedType === "wordCloud" || selectedType === "sentiment") {
          selectedColumns = selectedMetric ? [selectedMetric] : [];
        } else {
          selectedColumns = [
            ...(selectedMetric ? [selectedMetric] : []),
            ...(selectedDimension ? [selectedDimension] : []),
          ];
        }

        const payload = {
          card_type: cardTypeForApi,
          title,
          selected_columns: selectedColumns,
          metrics:
            selectedType === "wordCloud" || selectedType === "sentiment"
              ? undefined
              : metrics,
          dimensions:
            selectedType === "wordCloud" || selectedType === "sentiment"
              ? undefined
              : dimensions,
          card_filters: cardFilters,
          config: { width: 6, height: 2, x: 0, y: 0 },
        };

        await addInsightCard(targetTabId, payload);
        console.log("CRITICAL CARD PAYLOAD:", payload);
      }

      const refreshedCards = await refreshTabCardsWithData(targetTabId);

      setCardsByTab((prev) => ({
        ...prev,
        [targetTabId]: refreshedCards,
      }));

      message.success("Insight card added successfully.");

      // setAddOpen(false);
      setSelectedType(null);
      setTextTemp("");
      setSelectedMetric(undefined);
      setSelectedDimension(undefined);
      setSelectedFilterColumn(undefined);
      setSelectedFilterValues([]);
      setSelectedImageFile(null);
      setUploadedImageUrl("");
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add insight card.",
      );
    } finally {
      setLoading(false);
    }
  };

  const exportMenuItems: MenuProps["items"] = [
    { key: "csv", label: "Export CSV" },
    { key: "xlsx", label: "Export Excel" },
    { key: "pdf", label: "Export PDF" },
  ];

  return (
    <div className="dashboard-ai-layout" style={{ marginLeft: 0 }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 15,
          overflow: "hidden",
          border: "1.5px solid #C9A8F5",
        }}
      >
        {!showChooser && !loading && dashboard && (
          <div id="dashboard-header-printable">
            <HeaderSection
              title={dashTitle}
              subtitle={dashSubtitle}
              editingTitle={dashEditingTitle}
              headerOptions={dashHeaderOptions}
              setTitle={setDashTitle}
              setEditingTitle={setDashEditingTitle}
              onEdit={() => setPropOpen(true)}
            />
          </div>
        )}

        <div>
          <Modal
            open={showChooser && !openExisting}
            closable
            closeIcon={<CloseOutlined />}
            maskClosable={false}
            onCancel={() => setShowChooser(false)}
            footer={
              <Space>
                <Button
                  onClick={acceptTemplate}
                  loading={loading}
                  style={{ color: "#4A1C96", border: "1px solid #4A1C96" }}
                >
                  Accept this template
                </Button>
                <Button
                  onClick={chooseOwn}
                  disabled={loading}
                  style={{
                    color: "white",
                    background: "#4A1C96",
                    border: "1px solid #4A1C96",
                  }}
                >
                  <PlusOutlined /> Choose insights cards
                </Button>
              </Space>
            }
            className="dashboard-preview-modal"
          >
            <Title
              level={5}
              style={{
                marginBottom: "1rem",
                color: "#4A1C96",
                textAlign: "center",
                marginTop: "unset",
                fontFamily: "Poppins",
                fontSize: "22px",
              }}
            >
              Dashboard Preview
            </Title>
            <Text style={{ fontFamily: "Poppins", fontSize: "16px" }}>
              This is a preview of your dashboard. To customize it, click on the
              button below. <strong>You can add up to 10 cards per tab.</strong>
            </Text>
          </Modal>

          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: 40,
              }}
            >
              <Spin size="large" />
            </div>
          )}

          {!showChooser && !loading && dashboard && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 16,
                  padding: "12px 16px",
                  background: "#FDF9FF",
                  borderBottom: "1px solid #F4D9FF",
                }}
              >
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: exportMenuItems,
                    onClick: ({ key }) =>
                      gate.requirePaid(
                        () =>
                          handleExportDashboard(
                            key as "csv" | "xlsx" | "pdf",
                            activeKey,
                          ),
                        "Exporting the dashboard",
                      ),
                  }}
                >
                  <Button
                    icon={<DownloadOutlined />}
                    style={{
                      height: 36,
                      color: "#4A1C96",
                      borderColor: "#4A1C96",
                      fontWeight: 600,
                    }}
                  >
                    Export <DownOutlined />
                  </Button>
                </Dropdown>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() =>
                    gate.requirePaid(
                      () => setShareOpen(true),
                      "Sharing a public link",
                    )
                  }
                  style={{
                    color: "#4A1C96",
                    borderColor: "#4A1C96",
                    height: 36,
                    fontWeight: 600,
                  }}
                >
                  Share
                </Button>
                <div style={{ position: "relative", display: "inline-flex" }}>
                  <Button
                    onClick={() =>
                      gate.requirePaid(
                        () => setGenerateReportOpen(true),
                        "Generating a report",
                      )
                    }
                    style={{
                      background: "#A072EB",
                      color: "#fff",
                      border: "none",
                      height: 36,
                      fontWeight: 600,
                      paddingLeft: 16,
                      paddingRight: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M7 0L8.2 5.8L14 7L8.2 8.2L7 14L5.8 8.2L0 7L5.8 5.8L7 0Z"
                        fill="white"
                      />
                    </svg>
                    Generate Report
                  </Button>
                  <span
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#F59E0B",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                      pointerEvents: "none",
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                      <path
                        d="M5.5 0L6.4 4.1L11 5.5L6.4 6.9L5.5 11L4.6 6.9L0 5.5L4.6 4.1L5.5 0Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                </div>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setAddOpen(true)}
                  style={{
                    background: "#4A1C96",
                    color: "#fff",
                    borderColor: "#4A1C96",
                    height: 36,
                    fontWeight: 600,
                  }}
                >
                  Add insight card
                </Button>
                {Object.keys(drillDownFilters || {}).length > 0 && (
                  <Button onClick={handleClearDrillDown} style={{ height: 36 }}>
                    Clear filter
                  </Button>
                )}
              </div>

              <Tabs
                type="editable-card"
                hideAdd={false}
                className="dashboard-preview-tabs"
                tabBarStyle={{ margin: "0 16px", padding: "0 16px" }}
                items={tabItems}
                activeKey={activeKey || tabItems?.[0]?.key}
                onChange={(key) => {
                  setActiveKey(key);
                  setDrillDownFilters({});
                }}
                onEdit={(targetKey, action) => {
                  if (action === "add") {
                    handleAddTab();
                  }
                  if (action === "remove" && typeof targetKey === "string") {
                    handleDeleteTab(targetKey);
                  }
                }}
              />
            </>
          )}

          {!showChooser && !loading && !dashboard && (
            <div
              style={{
                padding: 24,
              }}
            >
              No dashboard data loaded yet.
            </div>
          )}
        </div>
      </div>

      <PropertiesDrawer
        open={propOpen}
        onClose={() => setPropOpen(false)}
        onSave={async () => {
          await handleSaveDashboardSettings({
            title: dashTitle,
            subtitle: dashSubtitle,
            headerOptions: dashHeaderOptions,
            footerLeft: dashFooterLeft,
            footerRight: dashFooterRight,
            footerOptions: dashFooterOptions,
          });
          setPropOpen(false);
        }}
        saving={saving}
        title={dashTitle}
        subtitle={dashSubtitle}
        setTitle={setDashTitle}
        setSubtitle={setDashSubtitle}
        headerOptions={dashHeaderOptions}
        setHeaderOptions={(upd: any) =>
          setDashHeaderOptions(upd(dashHeaderOptions))
        }
        footerLeft={dashFooterLeft}
        footerRight={dashFooterRight}
        setFooterLeft={setDashFooterLeft}
        setFooterRight={setDashFooterRight}
        footerOptions={dashFooterOptions}
        setFooterOptions={(upd: any) =>
          setDashFooterOptions(upd(dashFooterOptions))
        }
        logoUploadProps={logoUploadProps}
        headerImageUploadProps={headerImageUploadProps}
        footerImageUploadProps={footerImageUploadProps}
      />

      <Modal
        open={generateReportOpen}
        onCancel={() => setGenerateReportOpen(false)}
        footer={null}
        width={820}
        closeIcon={<CloseOutlined style={{ color: "#A072EB", fontSize: 22 }} />}
        centered
        className="generate-report-modal"
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h2 style={{ color: "#4A1C96", marginBottom: 8 }}>Generate Report</h2>
          <p style={{ margin: 0, color: "#444" }}>
            Choose a report type to generate insights from your data
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
            marginBottom: 32,
          }}
        >
          {[
            {
              key: "executive_brief",
              title: "Executive Brief",
              desc: "Provides a high-level summary of performance for busy decision-makers.",
              icon: <FileTextOutlined />,
            },
            {
              key: "operational_risk",
              title: "Operational Risk Report",
              desc: "To detect and explain anomalies or outliers in live data streams.",
              icon: <LineChartOutlined />,
            },
            {
              key: "market_sentiment",
              title: "Market Sentiment Narrative",
              desc: "Translates unstructured text and feedback into a human-centric story.",
              icon: <HeartOutlined />,
            },
            {
              key: "geographic_performance",
              title: "Geographic Performance Brief",
              desc: "Explains regional variations for sales or density-focused industries.",
              icon: <EnvironmentOutlined />,
            },
            {
              key: "periodic_growth_review",
              title: "Periodic Growth Review",
              desc: "A deep-dive into frequency and distributions over time.",
              icon: <RiseOutlined />,
            },
            {
              key: "user_engagement",
              title: "User Engagement Summary",
              desc: "Focuses on how users are interacting with the data points themselves.",
              icon: <UserOutlined />,
            },
          ].map((item) => {
            const active = selectedReportType === item.key;
            return (
              <div
                key={item.key}
                onClick={() => setSelectedReportType(item.key)}
                style={{
                  border: active ? "1.5px solid #A072EB" : "1px solid #e5e5e5",
                  background: active ? "#fbf5ff" : "#fff",
                  borderRadius: 6,
                  padding: 14,
                  cursor: "pointer",
                  minHeight: 96,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#A072EB",
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {item.icon}
                  {item.title}
                </div>
                <div style={{ color: "#555", fontSize: 13, lineHeight: 1.35 }}>
                  {item.desc}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <Button
            onClick={() => setGenerateReportOpen(false)}
            style={{
              height: 44,
              minWidth: 100,
              color: "#4A1C96",
              border: "1px solid #4A1C96",
            }}
          >
            Cancel
          </Button>
          <Button
            loading={reportLoading}
            onClick={async () => {
              if (!dashboardId) return;
              if (!gate.hasTokens()) {
                gate.requireTokens(0, () => {});
                return;
              }
              try {
                setReportLoading(true);
                setGenerateReportOpen(false);
                setReportPreviewOpen(true);
                const result = await generateDashboardReport(dashboardId, {
                  report_type: selectedReportType,
                  tab_id: activeKey || undefined,
                  filters:
                    Object.keys(drillDownFilters).length > 0
                      ? drillDownFilters
                      : undefined,
                });
                setReportData(result);
                gate.refresh();
              } catch (err: any) {
                message.error(
                  err?.response?.data?.message ||
                    err?.message ||
                    "Failed to generate report.",
                );
                setReportPreviewOpen(false);
              } finally {
                setReportLoading(false);
              }
            }}
            style={{
              height: 44,
              minWidth: 100,
              background: "#4A1C96",
              color: "#fff",
            }}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      <ShareModal
        open={shareOpen}
        dashboardId={dashboardId || ""}
        onClose={() => setShareOpen(false)}
      />

      <GenerateReportPreview
        open={reportPreviewOpen}
        loading={reportLoading}
        data={reportData}
        onClose={() => {
          setReportPreviewOpen(false);
          setReportData(null);
        }}
      />

      <AddInsightModal
        open={addOpen}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        textTemp={textTemp}
        setTextTemp={setTextTemp}
        onAdd={handleAddInsightCard}
        onClose={() => {
          setAddOpen(false);
          setSelectedType(null);
          setTextTemp("");
          setSelectedMetric(undefined);
          setSelectedDimension(undefined);
          setSelectedFilterColumn(undefined);
          setSelectedFilterValues([]);
          setSelectedImageFile(null);
          setUploadedImageUrl("");
          setCustomCardTitle("");
        }}
        goToDashboard={() => {
          setAddOpen(false);
          setSelectedType(null);
          setTextTemp("");
          setSelectedMetric(undefined);
          setSelectedDimension(undefined);
          setSelectedFilterColumn(undefined);
          setSelectedFilterValues([]);
          setSelectedImageFile(null);
          setUploadedImageUrl("");
          setCustomCardTitle("");
        }}
        loading={loading}
        cardLabels={cardLabels}
        columns={columns}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        selectedDimension={selectedDimension}
        setSelectedDimension={setSelectedDimension}
        selectedFilterColumn={selectedFilterColumn}
        setSelectedFilterColumn={setSelectedFilterColumn}
        selectedFilterValues={selectedFilterValues}
        setSelectedFilterValues={setSelectedFilterValues}
        selectedImageFile={selectedImageFile}
        setSelectedImageFile={setSelectedImageFile}
        uploadedImageUrl={uploadedImageUrl}
        setUploadedImageUrl={setUploadedImageUrl}
        customCardTitle={customCardTitle}
        setCustomCardTitle={setCustomCardTitle}
        isBscPinned={isBscPinned}
      />

      <Modal
        open={renameTabOpen}
        title="Rename tab"
        onCancel={() => {
          setRenameTabOpen(false);
          setTabToRename(null);
          setRenameTabValue("");
        }}
        onOk={handleRenameTab}
        okText="Save"
        className="rename-tab-style"
        footer={
          <Space>
            <Button
              onClick={() => {
                setRenameTabOpen(false);
                setTabToRename(null);
                setRenameTabValue("");
              }}
              style={{
                color: "#4A1C96",
                border: "1px solid #4A1C96",
                height: "44px",
                fontFamily: "Poppins",
                fontSize: "16px",
              }}
            >
              Cancel
            </Button>

            <Button onClick={handleRenameTab} className="save-button">
              Save
            </Button>
          </Space>
        }
      >
        <Input
          value={renameTabValue}
          onChange={(e) => setRenameTabValue(e.target.value)}
          placeholder="Enter tab name"
        />
      </Modal>
    </div>
  );
};

export default DashboardPreview;
